import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  CheckCircle as CheckoutIcon,
  Clear as ClearIcon,
  Storefront as StorefrontIcon,
  Build as BuildIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import type { CartTransferMode } from './cartTypes';

interface CartItem {
  itemId: string;
  name: string;
  quantity: number;
  maxQuantity: number;
  unit: string;
  transferSource: 'warehouse' | 'store' | 'taken';
}

interface ShoppingCartProps {
  open: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemoveItem: (itemId: string, transferSource?: CartTransferMode) => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onClearCart: () => void;
  onCheckout: () => void;
  activeCategory?: string;
  canTransfer?: boolean;
  cartTransferMode?: CartTransferMode;
  onCartTransferModeChange?: (mode: CartTransferMode) => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = ({
  open,
  onClose,
  items,
  onRemoveItem,
  onUpdateQuantity,
  onClearCart,
  onCheckout,
  activeCategory,
  canTransfer = false,
  cartTransferMode = 'warehouse',
  onCartTransferModeChange
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  const isSouvenirsTransfer = activeCategory === 'souvenirs' && canTransfer;
  const isTakenPoolTransfer =
    (activeCategory === 'workshop' || activeCategory === 'kitchen') && canTransfer;
  const isInventoryTransfer = isSouvenirsTransfer || isTakenPoolTransfer;
  const takenPoolI18n =
    activeCategory === 'kitchen' ? 'storage.kitchen' : 'storage.workshop';

  useEffect(() => {
    if (open) {
      setComment('');
      setCheckoutError(null);
      setCheckoutSuccess(null);
    }
  }, [open]);

  const handleQuantityChange = (itemId: string, newQuantity: string) => {
    const quantity = parseInt(newQuantity, 10);
    const foundItem = items.find(item => item.itemId === itemId);
    if (foundItem && quantity >= 1 && quantity <= foundItem.maxQuantity) {
      onUpdateQuantity(itemId, quantity);
    }
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;

    if (isSouvenirsTransfer) {
      const wrongSource = items.some(item => item.transferSource !== 'warehouse');
      if (wrongSource) {
        setCheckoutError(t('storage.souvenirs.cartWarehouseOnly', 'Krepšelyje turi būti tik prekės iš sandėliuko.'));
        return;
      }
    }

    setCheckoutLoading(true);
    setCheckoutError(null);
    setCheckoutSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_CHECKOUT}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: items.map(item => ({
            itemId: item.itemId,
            quantity: item.quantity
          })),
          comment: comment.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Checkout failed');
      }

      setCheckoutSuccess('Sėkmingai paimta iš sandėlio.');
      setTimeout(() => {
        setComment('');
        onClearCart();
        onClose();
        setCheckoutSuccess(null);
        onCheckout();
      }, 2000);
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError(error instanceof Error ? error.message : 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleInventoryTransfer = async (
    direction: 'to_store' | 'to_warehouse' | 'to_taken' | 'from_taken'
  ) => {
    if (items.length === 0) return;

    const expectedSource =
      direction === 'to_store' || direction === 'to_taken' ? 'warehouse'
        : direction === 'to_warehouse' ? 'store'
          : 'taken';
    const wrongSource = items.some(item => item.transferSource !== expectedSource);
    if (wrongSource) {
      if (isTakenPoolTransfer) {
        setCheckoutError(
          direction === 'to_taken'
            ? t(`${takenPoolI18n}.cartWarehouseOnly`)
            : t(`${takenPoolI18n}.cartTakenOnly`)
        );
      } else {
        setCheckoutError(
          direction === 'to_store'
            ? 'Krepšelyje turi būti tik prekės iš sandėliuko.'
            : 'Krepšelyje turi būti tik prekės iš parduotuvės.'
        );
      }
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);
    setCheckoutSuccess(null);

    const category =
      activeCategory === 'kitchen' ? 'kitchen'
        : activeCategory === 'workshop' ? 'workshop'
          : 'souvenirs';

    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_STORE_TRANSFER}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category,
          direction,
          items: items.map(item => ({
            itemId: item.itemId,
            quantity: item.quantity
          })),
          notes: comment.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Perkėlimas nepavyko');
      }

      const successMessages: Record<string, string> = {
        to_store: 'Prekės sėkmingai perkeltos į parduotuvę.',
        to_warehouse: 'Prekės sėkmingai grąžintos į sandėliuką.',
        to_taken: t(`${takenPoolI18n}.transferSuccess`),
        from_taken: t(`${takenPoolI18n}.returnSuccess`)
      };
      setCheckoutSuccess(successMessages[direction]);
      setTimeout(() => {
        setComment('');
        onClearCart();
        onClose();
        setCheckoutSuccess(null);
        onCheckout();
      }, 2000);
    } catch (error) {
      console.error('Inventory transfer error:', error);
      setCheckoutError(error instanceof Error ? error.message : 'Perkėlimas nepavyko');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: '#7b1fa2'
      }}>
        <CartIcon />
        Prekių krepšelis ({totalItems} prekių)
      </DialogTitle>

      <DialogContent>
        {checkoutSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {checkoutSuccess}
          </Alert>
        )}

        {checkoutError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {checkoutError}
          </Alert>
        )}

        {isSouvenirsTransfer && onCartTransferModeChange && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('storage.souvenirs.transferModeCartHint')}
            </Typography>
            <ToggleButtonGroup
              value={cartTransferMode}
              exclusive
              size="small"
              onChange={(_e, value: CartTransferMode | null) => {
                if (value && (value === 'warehouse' || value === 'store')) {
                  onCartTransferModeChange(value);
                }
              }}
            >
              <ToggleButton value="warehouse">
                <WarehouseIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.souvenirs.transferModeWarehouse')}
              </ToggleButton>
              <ToggleButton value="store">
                <StorefrontIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.souvenirs.transferModeStore')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {isTakenPoolTransfer && onCartTransferModeChange && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t(`${takenPoolI18n}.transferModeCartHint`)}
            </Typography>
            <ToggleButtonGroup
              value={cartTransferMode}
              exclusive
              size="small"
              onChange={(_e, value: CartTransferMode | null) => {
                if (value && (value === 'warehouse' || value === 'taken')) {
                  onCartTransferModeChange(value);
                }
              }}
            >
              <ToggleButton value="warehouse">
                <WarehouseIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t(`${takenPoolI18n}.transferModeWarehouse`)}
              </ToggleButton>
              <ToggleButton value="taken">
                <BuildIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t(`${takenPoolI18n}.transferModeTaken`)}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Krepšelis tuščias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isInventoryTransfer
                ? cartTransferMode === 'store' || cartTransferMode === 'taken'
                  ? isTakenPoolTransfer
                    ? t(`${takenPoolI18n}.cartEmptyTaken`)
                    : 'Pridėkite prekių iš parduotuvės'
                  : 'Pridėkite prekių iš sandėliuko'
                : 'Pridėkite prekių iš sandėliaus'}
            </Typography>
          </Box>
        ) : (
          <>
            <List>
              {items.map((item, index) => (
                <React.Fragment key={`${item.itemId}-${item.transferSource}`}>
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                          <Chip 
                            label={`${item.quantity} ${item.unit}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {isSouvenirsTransfer && (
                            <Chip
                              label={item.transferSource === 'store' ? 'Iš parduotuvės' : 'Iš sandėliuko'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {isTakenPoolTransfer && (
                            <Chip
                              label={item.transferSource === 'taken'
                                ? t(`${takenPoolI18n}.cartFromTaken`)
                                : t(`${takenPoolI18n}.transferModeWarehouse`)}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Galimas kiekis: {item.maxQuantity} {item.unit}
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          label="Kiekis"
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.itemId, e.target.value)}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">
                              {item.unit}
                            </InputAdornment>
                          }}
                          sx={{ width: 100 }}
                          inputProps={{
                            min: 1,
                            max: item.maxQuantity
                          }}
                        />
                        
                        <IconButton
                          edge="end"
                          onClick={() => onRemoveItem(item.itemId, item.transferSource)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  {index < items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <TextField
              label="Komentaras (neprivaloma)"
              placeholder="Priežastis arba pastaba"
              multiline
              minRows={1}
              maxRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
              inputProps={{ maxLength: 500 }}
            />

            <Paper sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Iš viso: {totalItems} prekių
                </Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={onClearCart}
                  color="error"
                >
                  Išvalyti krepšelį
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Uždaryti
        </Button>
        
        {items.length > 0 && isSouvenirsTransfer && (
          <>
            <Button
              variant="contained"
              startIcon={checkoutLoading ? <CircularProgress size={20} /> : <StorefrontIcon />}
              onClick={() => handleInventoryTransfer('to_store')}
              disabled={checkoutLoading || cartTransferMode !== 'warehouse'}
              sx={{ 
                backgroundColor: '#7b1fa2',
                '&:hover': { backgroundColor: '#6a1b9a' }
              }}
            >
              {checkoutLoading ? 'Apdorojama...' : 'Į parduotuvę'}
            </Button>
            <Button
              variant="outlined"
              startIcon={checkoutLoading ? <CircularProgress size={20} /> : <CheckoutIcon />}
              onClick={handleCheckout}
              disabled={checkoutLoading || cartTransferMode !== 'warehouse'}
              sx={{
                borderColor: '#7b1fa2',
                color: '#7b1fa2',
                '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#f3e5f5' }
              }}
            >
              {checkoutLoading ? 'Apdorojama...' : t('storage.checkout', 'Paimti prekes')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={checkoutLoading ? <CircularProgress size={20} /> : <WarehouseIcon />}
              onClick={() => handleInventoryTransfer('to_warehouse')}
              disabled={checkoutLoading || cartTransferMode !== 'store'}
            >
              {checkoutLoading ? 'Apdorojama...' : 'Atgal į sandėliuką'}
            </Button>
          </>
        )}

        {items.length > 0 && isTakenPoolTransfer && (
          <>
            <Button
              variant="contained"
              startIcon={checkoutLoading ? <CircularProgress size={20} /> : <BuildIcon />}
              onClick={() => handleInventoryTransfer('to_taken')}
              disabled={checkoutLoading || cartTransferMode !== 'warehouse'}
              sx={{
                backgroundColor: '#7b1fa2',
                '&:hover': { backgroundColor: '#6a1b9a' }
              }}
            >
              {checkoutLoading ? 'Apdorojama...' : t(`${takenPoolI18n}.transferButton`)}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={checkoutLoading ? <CircularProgress size={20} /> : <WarehouseIcon />}
              onClick={() => handleInventoryTransfer('from_taken')}
              disabled={checkoutLoading || cartTransferMode !== 'taken'}
            >
              {checkoutLoading ? 'Apdorojama...' : t(`${takenPoolI18n}.returnButton`)}
            </Button>
          </>
        )}

        {items.length > 0 && !isInventoryTransfer && (
          <Button
            variant="contained"
            startIcon={checkoutLoading ? <CircularProgress size={20} /> : <CheckoutIcon />}
            onClick={handleCheckout}
            disabled={checkoutLoading}
            sx={{ 
              backgroundColor: '#7b1fa2',
              '&:hover': { backgroundColor: '#6a1b9a' }
            }}
          >
            {checkoutLoading ? 'Apdorojama...' : t('storage.checkout', 'Paimti prekes')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShoppingCart;
